import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'

const AppContext = createContext(null)

export const AppProvider = ({ children }) => {
  const [session, setSession] = useState(null)
  const [user, setUser] = useState(null)

  const [loading, setLoading] = useState(true)
  const [access, setAccess] = useState([])
  const [employee, setEmployee] = useState(null)

  const getAccessAsync = async (rolName) => {
    if (!rolName) return

    try {
      const { data, error } = await supabase
        .from('rol_permisos')
        .select('*')
        .eq('rol_nombre', rolName)
        .ilike('vista_slug', '%ticket%')

      if (error) throw error

      setAccess(data || [])
      return data
    } catch (error) {
      console.error('Error obteniendo permisos:', error)
      setAccess([])
    }
  }

  const getEmployee = async (userId) => {
    try {
      const { data: kpisUser, error: kpisError } = await supabase
        .schema('KPIS')
        .from('User')
        .select('*')
        .eq('AuthId', userId)
        .maybeSingle()

      if (kpisError) throw kpisError

      if (!kpisUser) {
        setEmployee(null)
        return
      }

      const { data: empData, error: empError } = await supabase
        .schema('public')
        .from('Empleados')
        .select('*, roles (*)')
        .eq('id', kpisUser.UserMainId)
        .maybeSingle()
      if (empError) throw empError

      const rolName = empData?.roles?.nombre
      if (rolName) {
        await getAccessAsync(rolName)
      } else {
        setAccess([])
      }

      setEmployee(empData || null)
    } catch (error) {
      console.error('Error obteniendo empleado:', error)
      setEmployee(null)
      setAccess([])
    } finally {
      // Recién aquí la sesión está completamente hidratada (usuario + empleado
      // + permisos). Liberamos el gate de carga para que la app no se pinte
      // "sin usuario" en la primera carga.
      setLoading(false)
    }
  }

  useEffect(() => {
    let isMounted = true
    // Recordamos el último usuario procesado para evitar refetch redundante
    // en eventos que no cambian la identidad (p.ej. TOKEN_REFRESHED).
    let lastUserId = null

    // onAuthStateChange emite `INITIAL_SESSION` inmediatamente con la sesión
    // actual, así que NO hace falta un getSession() aparte: con un solo flujo
    // evitamos el doble fetch de empleado/permisos que re-renderizaba toda la app.
    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!isMounted) return

        const currentUser = session?.user ?? null
        setSession(session)
        setUser(currentUser)

        if (currentUser?.id) {
          if (currentUser.id !== lastUserId) {
            lastUserId = currentUser.id
            // Nuevo usuario: mantenemos loading=true hasta que getEmployee
            // termine de hidratar empleado + permisos (libera loading en su
            // finally). setTimeout(0) evita el deadlock conocido al usar
            // `await supabase` dentro del propio callback de onAuthStateChange.
            setTimeout(() => {
              if (isMounted) getEmployee(currentUser.id)
            }, 0)
          } else {
            // Mismo usuario (p.ej. TOKEN_REFRESHED): ya está hidratado.
            setLoading(false)
          }
        } else {
          // Sin sesión: estado limpio y se libera el gate de inmediato.
          lastUserId = null
          setEmployee(null)
          setAccess([])
          setLoading(false)
        }
      }
    )

    return () => {
      isMounted = false
      listener?.subscription?.unsubscribe()
    }
  }, [])

  const login = async (email, password) => {
    if (!email || !password) {
      throw new Error('Email y password son requeridos')
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      console.error('Login error:', error.message)
      throw error
    }

    return data
  }

  const logout = async () => {
    const { error } = await supabase.auth.signOut()

    if (error) {
      console.error('Logout error:', error.message)
      throw error
    }
  }

  const value = useMemo(
    () => ({
      supabase,
      session,
      user,
      loading,
      login,
      logout,
      isAuthenticated: !!user,
      employee,
      access,
    }),
    [session, user, loading, employee, access]
  )

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export const useApp = () => {
  const context = useContext(AppContext)

  if (!context) {
    throw new Error('useApp debe usarse dentro de AppProvider')
  }

  return context
}
